import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { usePageState } from "@/contexts/NavigationContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmtL, fmtNum, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { PlusIcon, MagnifyingGlassIcon, PencilSimpleIcon, TrashIcon, PackageIcon, FlowerIcon, EyeIcon, FileXlsIcon, DownloadSimpleIcon, UploadSimpleIcon, InfoIcon, ArrowCounterClockwiseIcon, CheckIcon } from "@phosphor-icons/react";
import ImageInput from "@/components/ImageInput";
import ExistenciaDetalle from "@/pages/_inventory/ExistenciaDetalle";
import { ExcelImportModal } from "@/pages/Backups";

const empty = {
  // Identificación
  codigo: "", sku: "", nombre: "", descripcion: "", codigo_barras: "", numero_parte_fabricante: "",
  tipo: "inventario_comercial",
  // Clasificación
  categoria: "", subcategoria: "", familia: "", marca: "",
  // Unidad y conversión
  unidad: "UND", unidad_secundaria: "", factor_conversion: 1,
  unidad_compra: "", factor_compra: 1, unidad_venta: "", factor_venta: 1,
  // Trazabilidad
  lote: "", lote_fabricacion: "", batch: "",
  fecha_fabricacion: "", fecha_vencimiento: "", fecha_test: "",
  // Stock
  stock_actual: 0, stock_minimo: 0, stock_maximo: 0, stock_minimo_bodegas: {}, stock_maximo_bodegas: {},
  ubicacion_bodega: "BOD-PRINCIPAL", proveedor_principal: "",
  // Costos / precios
  costo_promedio: 0, ultimo_costo: 0,
  precio_p0: 0, precio_p1: 0, precio_p2: 0, precio_p3: 0, precio_p4: 0,
  isv: 15,
  // Contabilidad
  cuenta_contable_codigo: "1104", cuenta_costo_codigo: "",
  // Estado y semillas
  es_semilla: false, activo: true,
  pc_peps: 0, pc_ponderado: 0,
  // Multimedia / notas
  imagen_url: "", ficha_tecnica_url: "", notas: "",
};

const TIPOS_PRODUCTO = [
  { v: "inventario_comercial", l: "Inventario comercial (se vende y se almacena)" },
  { v: "activo_fijo", l: "Activo fijo (uso interno, se deprecia)" },
  { v: "servicio", l: "Servicio (sin stock)" },
  { v: "no_inventariable", l: "No inventariable (consumo directo)" },
];

const CUENTAS_SUGERIDAS = {
  inventario_comercial: "1104",
  activo_fijo: "1201",
  servicio: "4101",
  no_inventariable: "6106",
};

// Búsqueda instantánea: sin mayúsculas ni tildes; todas las palabras deben aparecer en algún dato del producto
const normB = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const CAMPOS_BUSQUEDA = ["codigo", "sku", "nombre", "descripcion", "categoria", "subcategoria", "familia", "marca", "proveedor_principal", "codigo_barras"];
const textoBusqueda = (p) => normB(CAMPOS_BUSQUEDA.map((k) => p[k]).join(" "));

export default function Products() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = usePageState("q", "");
  const [hideZero, setHideZero] = useState(false);
  const [abiertos, setAbiertos] = useState({});   // VER MÁS: ingresos que componen la existencia
  const [showInactive, setShowInactive] = useState(false);
  const { user } = useAuth();
  const canCosts = user?.role === "admin";
  // Cargar precios desde Excel: exclusivo del Administrador General (el servidor también lo exige)
  const esAdminGeneral = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const [preciosOpen, setPreciosOpen] = useState(false);
  const sinPrecio = items.filter((x) => x.activo !== false
    && ["precio_p1", "precio_p2", "precio_p3", "precio_p4"].some((k) => !(Number(x[k]) > 0))).length;
  const sinCosto = items.filter((x) => x.activo !== false && x.tipo !== "servicio" && !(Number(x.costo_referencia) > 0)).length;
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  // Producto nuevo: código (prefijo del proveedor + secuencia), SKU (patrón del catálogo a partir del nombre) y
  // cuenta de ingreso por línea se llenan solos al elegir proveedor / escribir el nombre / elegir la subcategoría.
  // Solo se reemplaza lo que sigue siendo automático (lo que el usuario escribe a mano se respeta).
  const [codigoAuto, setCodigoAuto] = useState(null);   // última sugerencia {codigo, prefijo, sku, cuenta_ingreso}
  const elegirProveedor = (nombre) => setForm((f) => ({ ...f, proveedor_principal: nombre }));
  useEffect(() => {
    if (!open || editing || (!form.proveedor_principal && !form.nombre)) return undefined;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products/codigo-sugerido", { params: {
          proveedor: form.proveedor_principal || "", nombre: form.nombre || "", subcategoria: form.subcategoria || "", categoria: form.categoria || "" } });
        setForm((f) => {
          const prev = codigoAuto || {};
          const sigueAuto = (actual, anterior, defecto) => !actual || actual === anterior || (defecto !== undefined && actual === defecto);
          const n = { ...f };
          if (data.codigo && sigueAuto(f.codigo, prev.codigo)) n.codigo = data.codigo;
          if (data.sku && sigueAuto(f.sku, prev.sku)) n.sku = data.sku;
          const cta = data.cuenta_ingreso?.cuenta;
          if (cta && f.tipo === "inventario_comercial" && sigueAuto(f.cuenta_contable_codigo, prev.cuenta_ingreso?.cuenta, CUENTAS_SUGERIDAS[f.tipo]))
            n.cuenta_contable_codigo = cta;
          return n;
        });
        setCodigoAuto(data);
      } catch { /* la sugerencia es opcional */ }
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, form.proveedor_principal, form.nombre, form.subcategoria, form.categoria, form.tipo]);
  const esAuto = !editing && codigoAuto && form.codigo === codigoAuto.codigo;
  const skuAuto = !editing && codigoAuto && form.sku && form.sku === codigoAuto.sku;
  const cuentaAuto = !editing && codigoAuto?.cuenta_ingreso?.cuenta && form.cuenta_contable_codigo === codigoAuto.cuenta_ingreso.cuenta;

  // Derivar opciones únicas de productos existentes (para datalists)
  const indice = useMemo(() => items.map((p) => [p, textoBusqueda(p)]), [items]);
  const palabras = useMemo(() => normB(q).split(/\s+/).filter(Boolean), [q]);
  const encontrados = useMemo(() => (palabras.length ? indice.filter(([, t]) => palabras.every((w) => t.includes(w))).map(([p]) => p) : items),
    [indice, palabras, items]);
  const visibles = useMemo(() => encontrados.filter((p) => !hideZero || Number(p.stock_actual) > 0), [encontrados, hideZero]);
  const [sugAbierta, setSugAbierta] = useState(false);
  const [sugIdx, setSugIdx] = useState(0);
  const sugerencias = palabras.length ? encontrados.slice(0, 8) : [];
  const buscadorRef = useRef(null);
  const categorias = Array.from(new Set(items.map((x) => x.categoria).filter(Boolean))).sort();
  const subcategorias = Array.from(new Set(items
    .filter((x) => !form.categoria || x.categoria === form.categoria)
    .map((x) => x.subcategoria).filter(Boolean))).sort();
  const familias = Array.from(new Set(items
    .filter((x) => !form.subcategoria || x.subcategoria === form.subcategoria)
    .map((x) => x.familia).filter(Boolean))).sort();
  const marcas = Array.from(new Set(items.map((x) => x.marca).filter(Boolean))).sort();

  const load = async (inact = showInactive) => {
    setLoading(true);
    try {
      // se carga el catálogo completo; la búsqueda filtra al escribir (sin ir al servidor)
      const params = { ...(inact ? { include_inactive: true } : {}) };
      // Proveedores y catálogo de cuentas solo sirven para crear / editar productos: un rol de consulta (RTC) no los
      // pide, y si no tiene acceso no debe impedir que se cargue el catálogo
      const consulta = Boolean(user?.effective?.alcance_regional);
      const [{ data }, { data: whs }, { data: sups }, accResp] = await Promise.all([
        api.get("/products", { params }),
        api.get("/warehouses", { params: { activo: true, include_transito: false } }),
        consulta ? Promise.resolve({ data: [] }) : api.get("/suppliers").catch(() => ({ data: [] })),
        consulta ? Promise.resolve({ data: [] }) : api.get("/accounting/chart").catch(() => ({ data: [] })),
      ]);
      setItems(data);
      setWarehouses(whs);
      setSuppliers(sups);
      // Solo cuentas terminales que permiten movimiento — evita seleccionar agrupadoras/mayor
      setAccounts((accResp.data || []).filter((x) => x.tipo !== "encabezado" && (x.es_movimiento === undefined || x.es_movimiento === true)));
    } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.cuenta_contable_codigo || !accounts.find((a) => a.codigo === form.cuenta_contable_codigo)) {
      toast.error("Selecciona una cuenta contable válida del catálogo");
      return;
    }
    if (!editing && !String(form.codigo || "").trim() && !form.proveedor_principal) {
      toast.error("Elige el proveedor (sección ②) para generar el código, o escríbelo manualmente");
      return;
    }
    try {
      const payload = {
        ...form,
        stock_actual: Number(form.stock_actual),
        stock_minimo: Number(form.stock_minimo),
        stock_maximo: Number(form.stock_maximo),
        factor_conversion: Number(form.factor_conversion) || 1,
        factor_compra: Number(form.factor_compra) || 1,
        factor_venta: Number(form.factor_venta) || 1,
        stock_minimo_bodegas: numMap(form.stock_minimo_bodegas),
        stock_maximo_bodegas: numMap(form.stock_maximo_bodegas),
        costo_promedio: Number(form.costo_promedio),
        costo_catalogo: Number(form.costo_catalogo) || 0,
        ultimo_costo: Number(form.ultimo_costo) || Number(form.costo_promedio),
        precio_p0: Number(form.precio_p0),
        precio_p1: Number(form.precio_p1),
        precio_p2: Number(form.precio_p2),
        precio_p3: Number(form.precio_p3),
        precio_p4: Number(form.precio_p4),
        isv: Number(form.isv),
        es_semilla: !!form.es_semilla,
        activo: form.activo !== false,
      };
      if (editing) {
        // Costos y existencia NO se editan desde la ficha (los gestiona el inventario)
        ["stock_actual", "costo_promedio", "ultimo_costo", "pc_peps", "pc_ponderado", "p0_fijado_en", "p0_fijado_por",
          "costo_peps", "costo_referencia", "costo_origen"].forEach((k) => delete payload[k]);
        if (!esAdminGeneral) delete payload.costo_catalogo;
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        // código automático: el servidor asigna el siguiente de la secuencia del proveedor al guardar (sin repetir)
        if (esAuto) payload.codigo = "";
        const { data: creado } = await api.post("/products", payload);
        toast.success(`Producto ${creado.codigo} creado`);
      }
      setOpen(false); setForm(empty); setEditing(null); setCodigoAuto(null);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };
  const pm = useModalForm({ value: form, abierto: open, onClose: () => setOpen(false), onSave });

  const onEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };
  const onReactivate = async (p) => {
    try { await api.post(`/products/${p.id}/reactivate`); toast.success(`${p.codigo} reactivado`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const onDel = async (p) => {
    if (!window.confirm(`¿Eliminar ${p.codigo} - ${p.categoria}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Producto eliminado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Layout
      title="Productos e Inventario"
      subtitle={`${items.length} productos${showInactive ? "" : " activos"} · Doble lectura PC PEPS / PC ponderado · Niveles P0–P4`}
      actions={
        <div className="flex gap-2 flex-wrap">
          <ExcelButtons onChanged={load} />
          {esAdminGeneral && <button
            data-testid="cargar-precios-btn"
            onClick={() => setPreciosOpen(true)}
            title="Descargar la plantilla con los productos sin precio, llenarla y subirla"
            className="border border-agro-yellow/40 hover:bg-agro-yellow/10 text-agro-yellow rounded-md px-3 py-2 text-sm flex items-center gap-2 transition-all"
            type="button"
          >Cargar precios{sinPrecio > 0 && <span className="text-[11px] bg-agro-yellow/20 rounded px-1.5" data-testid="sin-precio-count">{sinPrecio} sin precio</span>}
            {sinCosto > 0 && <span className="text-[11px] bg-red-500/20 text-red-300 rounded px-1.5" data-testid="sin-costo-count">{sinCosto} sin costo</span>}</button>}
          <button
            data-testid="goto-adjustments-btn"
            onClick={() => navigate("/inventario/ajustes")}
            className="border border-border hover:border-agro-green/40 text-text-secondary hover:text-text-primary rounded-md px-3 py-2 text-sm flex items-center gap-2 transition-all"
            type="button"
          >Ajustes</button>
          {canCosts && <button
            data-testid="goto-double-cost-btn"
            onClick={() => navigate("/inventario/doble-lectura")}
            className="border border-border hover:border-agro-yellow/40 text-text-secondary hover:text-text-primary rounded-md px-3 py-2 text-sm flex items-center gap-2 transition-all"
            type="button"
          >Doble lectura</button>}
          <button
            data-testid="new-product-btn"
            onClick={() => {
              setEditing(null); setCodigoAuto(null);
              // Preselecciona la cuenta sugerida según tipo, si existe en el catálogo cargado
              const sugerido = CUENTAS_SUGERIDAS[empty.tipo];
              const existe = sugerido && accounts.find((a) => a.codigo === sugerido);
              setForm({ ...empty, cuenta_contable_codigo: existe ? sugerido : (accounts[0]?.codigo || "") });
              setOpen(true);
            }}
            className="bg-agro-green hover:bg-agro-green-hover text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 transition-all active:scale-95"
          >
            <PlusIcon size={16} weight="bold" /> Nuevo producto
          </button>
        </div>
      }
    >
      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border/60 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-64" ref={buscadorRef}>
            <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              data-testid="products-search"
              value={q}
              autoComplete="off"
              onChange={(e) => { setQ(e.target.value); setSugAbierta(true); setSugIdx(0); }}
              onFocus={() => setSugAbierta(true)}
              onBlur={() => setTimeout(() => setSugAbierta(false), 150)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") { e.preventDefault(); setSugAbierta(true); setSugIdx((i) => Math.min(i + 1, sugerencias.length - 1)); }
                else if (e.key === "ArrowUp") { e.preventDefault(); setSugIdx((i) => Math.max(i - 1, 0)); }
                else if (e.key === "Enter" && sugAbierta && sugerencias[sugIdx]) { e.preventDefault(); navigate(`/productos/${sugerencias[sugIdx].id}`); }
                else if (e.key === "Escape") setSugAbierta(false);
              }}
              placeholder="Escribe código, SKU, nombre, marca, proveedor… (los resultados salen al escribir)"
              className="w-full bg-app border border-border rounded-md pl-9 pr-16 py-2 text-sm focus:border-agro-green"
            />
            {q && <button type="button" onClick={() => { setQ(""); setSugAbierta(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-text-muted hover:text-text-primary" data-testid="products-search-clear">Limpiar</button>}
            {sugAbierta && sugerencias.length > 0 && (
              <div className="absolute z-30 left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-xl max-h-80 overflow-y-auto" data-testid="products-search-list">
                {sugerencias.map((p, i) => (
                  <button key={p.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => navigate(`/productos/${p.id}`)}
                    onMouseEnter={() => setSugIdx(i)} data-testid={`products-search-opt-${p.codigo}`}
                    className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-border/40 last:border-0 ${i === sugIdx ? "bg-agro-green/10" : "hover:bg-white/[0.03]"}`}>
                    <span className="font-mono text-xs text-agro-green w-28 shrink-0 whitespace-nowrap">{p.codigo}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm text-text-primary truncate">{p.nombre || p.descripcion}</span>
                      <span className="block text-[10px] text-text-muted truncate">{[p.sku, p.proveedor_principal, p.subcategoria || p.categoria].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span className="text-xs text-text-secondary shrink-0">{fmtNum(p.stock_actual)} {p.unidad || ""}</span>
                  </button>
                ))}
                {encontrados.length > sugerencias.length && <div className="px-3 py-1.5 text-[10px] text-text-muted">… y {encontrados.length - sugerencias.length} más en la tabla</div>}
              </div>
            )}
          </div>
          <span className="text-xs text-text-muted" data-testid="products-search-count">{palabras.length ? `${visibles.length} de ${items.length}` : `${items.length} productos`}</span>
          <button
            onClick={() => setHideZero((v) => !v)}
            data-testid="toggle-hide-zero-stock"
            className={`text-xs px-3 py-2 rounded-md border transition-all flex items-center gap-1.5 ${
              hideZero ? "bg-agro-green/10 border-agro-green/40 text-agro-green" : "border-border text-text-secondary hover:border-agro-green/40"
            }`}
            title="Mostrar únicamente productos con inventario disponible"
          >
            <span className={`w-3 h-3 rounded-sm border flex items-center justify-center ${hideZero ? "bg-agro-green border-agro-green" : "border-text-muted"}`}>
              {hideZero && <CheckIcon size={10} weight="bold" className="text-black" aria-hidden="true" />}
            </span>
            Ocultar existencia 0
          </button>
          <label className="text-xs px-3 py-2 rounded-md border border-border text-text-secondary flex items-center gap-1.5 cursor-pointer" title="Incluir productos inactivos (para reactivarlos)">
            <input type="checkbox" data-testid="toggle-inactive-products" checked={showInactive}
              onChange={(e) => { setShowInactive(e.target.checked); load(e.target.checked); }} className="accent-agro-green" />
            Inactivos
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-sidebar">
              <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                <th className="p-3 pl-5">Producto</th>
                <th className="p-3 text-right">Stock</th>
                {canCosts && <th className="p-3 text-right">PC PEPS</th>}
                {canCosts && <th className="p-3 text-right">PC ponderado</th>}
                <th className="p-3 text-right">P1 venta</th>
                <th className="p-3">ISV</th>
                <th className="p-3 text-right pr-5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : visibles.length === 0 ? (
                <tr><td colSpan="7" className="p-12 text-center text-text-muted">
                  <PackageIcon size={32} className="inline mb-2 opacity-40" />
                  <div>{hideZero ? "No hay productos con existencia disponible" : "No hay productos"}</div>
                </td></tr>
              ) : visibles.map((p) => {
                const low = p.stock_actual <= p.stock_minimo;
                const abierto = Boolean(abiertos[p.id]);
                return (
                  <Fragment key={p.id}>
                  <tr className={`border-t border-border/40 hover:bg-white/[0.02] ${p.activo === false ? "opacity-60" : ""}`} data-testid={`product-row-${p.codigo}`}>
                    <td className="p-3 pl-5">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-agro-green" data-testid={`product-code-${p.codigo}`}>{p.codigo}</span>
                        <span className="text-text-muted text-sm">·</span>
                        <span className="text-base font-semibold text-text-primary leading-tight" data-testid={`product-name-${p.codigo}`}>
                          {p.nombre}
                        </span>
                        {p.activo === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/30">Inactivo</span>}
                        {p.es_semilla && (
                          <span title="Producto semilla (PEPS por fecha de test)" className="text-agro-yellow">
                            <FlowerIcon size={14} weight="fill" />
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-text-muted">
                        {p.sku && <span>SKU: {p.sku}</span>}
                        {p.marca && <span>· {p.marca}</span>}
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <span className={`text-sm font-medium ${low ? "text-agro-yellow" : "text-text-primary"}`}>{fmtNum(p.stock_actual)} {p.unidad}</span>
                      <div className="text-[10px] text-text-muted">min {fmtNum(p.stock_minimo)}</div>
                      <button type="button" onClick={() => setAbiertos((a) => ({ ...a, [p.id]: !a[p.id] }))}
                        data-testid={`ver-mas-${p.codigo}`} aria-expanded={abierto}
                        className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-agro-green hover:underline">
                        {abierto ? "VER MENOS ▲" : "VER MÁS ▼"}
                      </button>
                    </td>
                    {canCosts && <td className="p-3 text-right text-sm font-medium text-text-primary" data-testid={`pc-peps-${p.codigo}`}>{Number(p.costo_referencia) > 0 ? fmtL(p.costo_peps || p.costo_referencia) : "—"}{p.costo_origen === "catalogo" && <span className="block text-[10px] text-text-muted" title="El producto aún no tiene ingresos con costo">catálogo</span>}</td>}
                    {canCosts && <td className="p-3 text-right text-sm text-text-secondary" data-testid={`pc-pond-${p.codigo}`}>{Number(p.pc_ponderado || p.costo_promedio || p.costo_catalogo) > 0 ? fmtL(p.pc_ponderado || p.costo_promedio || p.costo_catalogo) : "—"}</td>}
                    <td className="p-3 text-right text-sm font-medium text-agro-green">{fmtL(p.precio_p1)}</td>
                    <td className="p-3 text-xs"><span className="px-2 py-0.5 rounded bg-app border border-border text-text-secondary">{p.isv}%</span></td>
                    <td className="p-3 pr-5 text-right">
                      <button onClick={() => navigate(`/productos/${p.id}`)} className="p-1.5 text-text-muted hover:text-agro-yellow" data-testid={`view-product-${p.codigo}`} title="Ver detalle PEPS"><EyeIcon size={16} /></button>
                      <button aria-label="Editar" onClick={() => onEdit(p)} className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`edit-product-${p.codigo}`}><PencilSimpleIcon size={16} /></button>
                      {p.activo === false
                        ? <button onClick={() => onReactivate(p)} title="Reactivar" className="p-1.5 text-text-muted hover:text-agro-green" data-testid={`reactivate-product-${p.codigo}`}><ArrowCounterClockwiseIcon size={16} /></button>
                        : <button aria-label="Eliminar" onClick={() => onDel(p)} className="p-1.5 text-text-muted hover:text-red-400" data-testid={`delete-product-${p.codigo}`}><TrashIcon size={16} /></button>}
                    </td>
                  </tr>
                  {abierto && (
                    <tr><td colSpan="7" className="p-0"><ExistenciaDetalle producto={p} /></td></tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...pm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="product-modal">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar producto" : "Nuevo producto"}</h3>
              <BotonCerrar onClick={pm.cerrar} />
            </div>
            <form onSubmit={pm.guardar} className="p-5 space-y-5">
              {/* ===== Sección 1: Identificación ===== */}
              <Section title="① Identificación" subtitle="Datos básicos para reconocer el producto.">
                <SelectField label="Tipo *" v={form.tipo} testid="form-tipo"
                  on={(v) => {
                    const sugerido = CUENTAS_SUGERIDAS[v];
                    const existe = sugerido && accounts.find((a) => a.codigo === sugerido);
                    setForm({ ...form, tipo: v, cuenta_contable_codigo: existe ? sugerido : form.cuenta_contable_codigo });
                  }}
                  opts={TIPOS_PRODUCTO} />
                {editing ? <Field label="Código *" v={form.codigo} on={(v) => setForm({ ...form, codigo: v })} required testid="form-codigo" /> : (
                  <div data-testid="form-codigo-auto">
                    <Field label="Código *" v={form.codigo} on={(v) => setForm({ ...form, codigo: v.toUpperCase() })} testid="form-codigo" />
                    <p className="text-[10px] text-text-muted mt-1">
                      {esAuto ? <>Automático · prefijo <b>{codigoAuto.prefijo}</b> del proveedor, siguiente de su secuencia (se confirma al guardar).</>
                        : form.codigo ? "Código escrito manualmente." : "Se genera al elegir el proveedor (sección ②)."}
                    </p>
                  </div>
                )}
                <div>
                  <Field label="SKU" v={form.sku} on={(v) => setForm({ ...form, sku: v.toUpperCase() })} testid="form-sku" />
                  {!editing && <p className="text-[10px] text-text-muted mt-1" data-testid="form-sku-nota">
                    {skuAuto ? "Automático a partir del nombre (patrón del catálogo)." : form.sku ? "SKU escrito manualmente." : "Se genera al escribir el nombre."}</p>}
                </div>
                <Field label="Nombre comercial *" v={form.nombre} on={(v) => setForm({ ...form, nombre: v })} testid="form-nombre" required />
                <Field label="Código de barras" v={form.codigo_barras} on={(v) => setForm({ ...form, codigo_barras: v })} testid="form-codigo-barras" />
                <Field label="N° parte fabricante (NPF)" v={form.numero_parte_fabricante} on={(v) => setForm({ ...form, numero_parte_fabricante: v })} testid="form-npf" />
              </Section>

              {/* ===== Sección 2: Clasificación multinivel ===== */}
              <Section title="② Clasificación" subtitle="Categoría → Subcategoría → Familia (3 niveles).">
                <DataListField label="Categoría" v={form.categoria}
                  on={(v) => setForm({ ...form, categoria: v, subcategoria: "", familia: "" })}
                  options={categorias} testid="form-categoria" placeholder="Elige o escribe…" />
                <DataListField label="Subcategoría" v={form.subcategoria}
                  on={(v) => setForm({ ...form, subcategoria: v, familia: "" })}
                  options={subcategorias} testid="form-subcategoria" placeholder="Elige o escribe…" />
                <DataListField label="Familia / Línea" v={form.familia}
                  on={(v) => setForm({ ...form, familia: v })}
                  options={familias} testid="form-familia" placeholder="Elige o escribe…" />
                <DataListField label="Marca" v={form.marca} on={(v) => setForm({ ...form, marca: v })}
                  options={marcas} testid="form-marca" placeholder="Elige o escribe…" />
                <SelectField label="Proveedor (línea)" v={form.proveedor_principal}
                  on={elegirProveedor} testid="form-proveedor"
                  opts={suppliers.map((s) => ({ v: s.nombre, l: `${s.codigo || ""} ${s.nombre}`.trim() }))}
                  placeholder="— Selecciona proveedor —" />
                <SelectField label="ISV" v={form.isv} on={(v) => setForm({ ...form, isv: v })} testid="form-isv"
                  opts={[{v:0,l:"Exento 0%"},{v:15,l:"15%"},{v:18,l:"18%"}]} />
              </Section>

              {/* ===== Sección 3: Unidad y conversión ===== */}
              <Section title="③ Unidad de medida y conversión" subtitle="Define la unidad principal y, opcionalmente, una secundaria con factor.">
                <SelectField label="Unidad principal *" v={form.unidad} on={(v) => setForm({ ...form, unidad: v })} testid="form-unidad" opts={[
                  { v: "UND", l: "Unidad (UND)" }, { v: "KG", l: "Kilogramo (KG)" }, { v: "LB", l: "Libra (LB)" },
                  { v: "QQ", l: "Quintal (QQ)" }, { v: "TM", l: "Tonelada métrica (TM)" },
                  { v: "MK", l: "Millar (MK) — 1 MK = 1,000 semillas" },
                  { v: "LT", l: "Litro (LT)" }, { v: "ML", l: "Mililitro (ML)" }, { v: "GAL", l: "Galón (GAL)" },
                  { v: "MT", l: "Metro (MT)" }, { v: "CM", l: "Centímetro (CM)" },
                  { v: "PAQ", l: "Paquete (PAQ)" }, { v: "CJ", l: "Caja (CJ)" }, { v: "SCO", l: "Saco (SCO)" },
                ]} />
                <SelectField label="Unidad secundaria" v={form.unidad_secundaria}
                  on={(v) => setForm({ ...form, unidad_secundaria: v })} testid="form-unidad-sec"
                  opts={[{ v: "", l: "— Ninguna —" }, { v: "UND", l: "UND" }, { v: "KG", l: "KG" }, { v: "LB", l: "LB" }, { v: "LT", l: "LT" }, { v: "GAL", l: "GAL" }]} />
                <Field label={`Factor (1 ${form.unidad} = N ${form.unidad_secundaria || "—"})`} type="number"
                  v={form.factor_conversion} on={(v) => setForm({ ...form, factor_conversion: v })} testid="form-factor" />
                <Field label="Unidad de compra (vacío = almacén)" v={form.unidad_compra} on={(v) => setForm({ ...form, unidad_compra: v.toUpperCase() })} testid="form-unidad-compra" />
                <Field label={`1 ${form.unidad_compra || "compra"} = N ${form.unidad}`} type="number" v={form.factor_compra} on={(v) => setForm({ ...form, factor_compra: v })} testid="form-factor-compra" />
                <div className="text-[10px] text-text-muted self-end pb-2">Al recibir una OC la cantidad se convierte de unidad de compra a unidad de almacén.</div>
                <Field label="Unidad de venta (vacío = almacén)" v={form.unidad_venta} on={(v) => setForm({ ...form, unidad_venta: v.toUpperCase() })} testid="form-unidad-venta" />
                <Field label={`1 ${form.unidad_venta || "venta"} = N ${form.unidad}`} type="number" v={form.factor_venta} on={(v) => setForm({ ...form, factor_venta: v })} testid="form-factor-venta" />
              </Section>

              {/* Distinción producto/semilla — se mantiene solo el flag; el resto (stock, lote, fechas, precios) se captura en el módulo de Ingresos. */}
              {form.tipo !== "servicio" && (
                <Section title="④ Categoría de inventario" subtitle="Semilla se rige por fecha de germinación (test); el resto por fecha de vencimiento.">
                  <div className="md:col-span-3">
                    <div className="flex gap-3" data-testid="form-categoria-inv">
                      <label className={`flex-1 cursor-pointer border rounded-md px-3 py-2 text-sm flex items-center gap-2 transition-colors ${!form.es_semilla ? "border-agro-green bg-agro-green/10 text-agro-green" : "border-border text-text-secondary hover:border-border/80"}`}>
                        <input type="radio" name="categoria-inv" data-testid="form-cat-producto"
                          checked={!form.es_semilla}
                          onChange={() => setForm({ ...form, es_semilla: false })}
                          className="accent-agro-green" />
                        <PackageIcon size={16} /> Producto <span className="text-[10px] text-text-muted ml-auto">(usa fecha venc.)</span>
                      </label>
                      <label className={`flex-1 cursor-pointer border rounded-md px-3 py-2 text-sm flex items-center gap-2 transition-colors ${form.es_semilla ? "border-agro-yellow bg-agro-yellow/10 text-agro-yellow" : "border-border text-text-secondary hover:border-border/80"}`}>
                        <input type="radio" name="categoria-inv" data-testid="form-cat-semilla"
                          checked={!!form.es_semilla}
                          onChange={() => setForm({ ...form, es_semilla: true })}
                          className="accent-agro-yellow" />
                        <FlowerIcon size={16} weight="fill" /> Semilla <span className="text-[10px] text-text-muted ml-auto">(usa fecha de test)</span>
                      </label>
                    </div>
                  </div>
                </Section>
              )}

              {/* ===== Precio de costo de catálogo (solo Administrador General) ===== */}
              {esAdminGeneral && form.tipo !== "servicio" && (
                <Section title="Precio de costo" subtitle="Costo de catálogo: se usa mientras el producto no tenga ingresos con costo. Con ingresos, el sistema muestra el costo real (PEPS / ponderado).">
                  <Field label="Precio de costo de catálogo (L, sin ISV)" type="number" v={form.costo_catalogo ?? ""}
                    on={(v) => setForm({ ...form, costo_catalogo: v })} testid="form-costo-catalogo" />
                  {editing && Number(editing.costo_peps || editing.pc_ponderado) > 0 ? (
                    <div className="text-xs text-text-secondary self-end pb-2" data-testid="form-costo-ingresos">
                      Costo de sus ingresos: PEPS {fmtL(editing.costo_peps || 0)} · ponderado {fmtL(editing.pc_ponderado || 0)}
                    </div>
                  ) : <div />}
                  <div />
                </Section>
              )}

              {/* ===== Punto de reorden ===== */}
              {form.tipo !== "servicio" && (
                <Section title="Punto de reorden" subtitle="Auto-OC sugiere reordenar cuando la existencia llega al mínimo (hasta el máximo). Opcional por bodega.">
                  <Field label="Stock mínimo (general)" type="number" v={form.stock_minimo} on={(v) => setForm({ ...form, stock_minimo: v })} testid="form-stock-min" />
                  <Field label="Stock máximo (general)" type="number" v={form.stock_maximo} on={(v) => setForm({ ...form, stock_maximo: v })} testid="form-stock-max" />
                  <div />
                  {warehouses.map((w) => (
                    <div key={w.id} className="md:col-span-3 grid grid-cols-3 gap-3 items-end">
                      <div className="text-xs text-text-secondary pb-2 font-mono">{w.codigo} · {w.nombre}</div>
                      <Field label="Mínimo bodega" type="number" v={(form.stock_minimo_bodegas || {})[w.id] ?? ""}
                        on={(v) => setForm({ ...form, stock_minimo_bodegas: { ...(form.stock_minimo_bodegas || {}), [w.id]: v } })} testid={`form-min-${w.codigo}`} />
                      <Field label="Máximo bodega" type="number" v={(form.stock_maximo_bodegas || {})[w.id] ?? ""}
                        on={(v) => setForm({ ...form, stock_maximo_bodegas: { ...(form.stock_maximo_bodegas || {}), [w.id]: v } })} testid={`form-max-${w.codigo}`} />
                    </div>
                  ))}
                </Section>
              )}

              {/* ===== Sección 5: Contabilidad ===== */}
              <Section title="⑤ Contabilidad" subtitle="Cuenta contable a la que se cargará el costo al ingresar/vender.">
                <div className="md:col-span-3">
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">
                    Cuenta contable * {cuentaAuto
                      ? <span className="text-agro-green normal-case" data-testid="form-cuenta-auto">(automática: {codigoAuto.cuenta_ingreso.nombre} · por {codigoAuto.cuenta_ingreso.motivo})</span>
                      : <span className="text-agro-yellow normal-case">(sugerida: {CUENTAS_SUGERIDAS[form.tipo] || "1104"})</span>}
                  </label>
                  <select
                    value={form.cuenta_contable_codigo}
                    onChange={(e) => setForm({ ...form, cuenta_contable_codigo: e.target.value })}
                    data-testid="form-cuenta"
                    required
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
                  >
                    <option value="">— Selecciona una cuenta del catálogo —</option>
                    {accounts.map((a) => (
                      <option key={a.codigo} value={a.codigo}>
                        {a.codigo} — {a.nombre} {a.tipo ? `· ${a.tipo}` : ""}
                      </option>
                    ))}
                  </select>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 mt-3 block">
                    Cuenta de costo de venta <span className="text-text-muted normal-case">(vacío = 5101)</span>
                  </label>
                  <select value={form.cuenta_costo_codigo || ""} onChange={(e) => setForm({ ...form, cuenta_costo_codigo: e.target.value })}
                    data-testid="form-cuenta-costo"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green">
                    <option value="">— Costo de ventas por defecto (5101) —</option>
                    {accounts.map((a) => <option key={a.codigo} value={a.codigo}>{a.codigo} — {a.nombre}</option>)}
                  </select>
                  {accounts.length === 0 && (
                    <p className="text-[11px] text-agro-yellow mt-1.5">
                      No hay cuentas contables en el catálogo. Impórtalas desde <strong>Copias de Seguridad → Importador → Catálogo de Cuentas</strong>.
                    </p>
                  )}
                </div>
              </Section>

              {/* ===== Imagen y notas ===== */}
              <Section title="Imagen y notas" subtitle="Imagen del producto (subida a almacenamiento) y notas internas.">
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Imagen</label>
                  <ImageInput value={form.imagen_url || ""} onChange={(v) => setForm({ ...form, imagen_url: v })} folder="products" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">Notas internas</label>
                  <textarea rows={4} value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                    data-testid="form-notas" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
                </div>
              </Section>

              {/* Aviso: stock, lote, fechas, costo y precios se capturan al hacer un ingreso */}
              <div className="bg-agro-yellow/10 border border-agro-yellow/40 rounded-md p-3 flex items-start gap-2">
                <InfoIcon size={16} className="text-agro-yellow flex-shrink-0 mt-0.5" />
                <div className="text-xs text-text-secondary">
                  El <strong className="text-agro-yellow">stock, batch, lote, fechas, costo y precios de venta</strong> se capturan al registrar cualquier <strong>ingreso</strong> en el módulo <em>Ingresos (recepción)</em>. Este catálogo sólo guarda la identidad y clasificación del producto.
                </div>
              </div>

              {/* ===== Estado activo/inactivo ===== */}
              <div className="flex items-center gap-3 p-3 rounded-md bg-app border border-border">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="form-activo"
                    checked={form.activo !== false}
                    onChange={(e) => setForm({ ...form, activo: e.target.checked })}
                    className="accent-agro-green w-4 h-4" />
                  <span className="text-sm text-text-primary">Producto activo (disponible para venta/inventario)</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-border/60">
                <button type="button" onClick={pm.cerrar}
                  className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary hover:text-text-primary">Cancelar</button>
                <button type="submit" data-testid="product-save-btn" disabled={pm.saving || !(form.codigo && form.nombre)}
                  className="px-5 py-2 text-sm bg-agro-green text-black font-medium rounded-md hover:bg-agro-green-hover disabled:opacity-40">{pm.saving ? "Guardando…" : "Guardar producto"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {preciosOpen && (
        <ExcelImportModal initialEntity="precios" initialFiltro={sinCosto > 0 ? "sin_costo" : "sin_precio"}
          onClose={() => { setPreciosOpen(false); load(); }}
          onDone={() => { setPreciosOpen(false); load(); }} />
      )}
    </Layout>
  );
}

function numMap(m) {
  const out = {};
  Object.entries(m || {}).forEach(([k, v]) => { if (v !== "" && v !== null && !isNaN(Number(v)) && Number(v) > 0) out[k] = Number(v); });
  return out;
}

function Section({ title, subtitle, children }) {
  return (
    <fieldset className="border border-border/60 rounded-lg p-4 pt-3 bg-app/40">
      <legend className="px-2 text-[11px] uppercase tracking-wider text-agro-yellow font-semibold">{title}</legend>
      {subtitle && <p className="text-[11px] text-text-muted mb-3 -mt-1 leading-relaxed">{subtitle}</p>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {children}
      </div>
    </fieldset>
  );
}

function Field({ label, v, on, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={v}
        required={required}
        onChange={(e) => on(e.target.value)}
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
      />
    </div>
  );
}
function SelectField({ label, v, on, opts, testid, placeholder }) {
  // Detecta si los valores son numéricos (ISV) o strings (bodega, proveedor, unidad)
  const isNumeric = opts.length > 0 && typeof opts[0].v === "number";
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <select
        data-testid={testid}
        value={v ?? ""}
        onChange={(e) => on(isNumeric ? Number(e.target.value) : e.target.value)}
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {opts.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

function DataListField({ label, v, on, options = [], testid, placeholder }) {
  // Combobox: lista predefinida + permite escribir un nuevo valor
  const id = `dl-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{label}</label>
      <input
        data-testid={testid}
        list={id}
        value={v ?? ""}
        placeholder={placeholder}
        onChange={(e) => on(e.target.value)}
        autoComplete="off"
        className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green"
      />
      <datalist id={id}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </div>
  );
}

function ExcelButtons({ onChanged }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    window.URL.revokeObjectURL(url);
  };

  const rm = useModalForm({ abierto: Boolean(result), onClose: () => setResult(null) });
  const onTemplate = async () => {
    setBusy(true);
    try {
      const res = await api.get("/products/import/template", { responseType: "blob" });
      downloadFile(res.data, "plantilla_productos_agroport.xlsx");
      toast.success("Plantilla descargada");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const onExport = async () => {
    setBusy(true);
    try {
      const res = await api.get("/products/export", { responseType: "blob" });
      downloadFile(res.data, "productos_agroport.xlsx");
      toast.success("Productos exportados");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Importado: ${data.creados} creados, ${data.actualizados} actualizados, ${data.errores?.length || 0} errores`);
      onChanged && onChanged();
    } catch (er) { toast.error(apiErr(er)); }
    finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <div className="flex gap-1 border border-border rounded-md p-0.5" data-testid="excel-toolbox">
        <button onClick={onTemplate} disabled={busy} title="Descargar plantilla Excel"
                className="px-2.5 py-1.5 text-xs text-text-secondary hover:text-agro-green flex items-center gap-1 disabled:opacity-50"
                data-testid="products-template-btn">
          <FileXlsIcon size={14} /> Plantilla
        </button>
        <button onClick={onExport} disabled={busy} title="Exportar todos los productos a Excel"
                className="px-2.5 py-1.5 text-xs text-text-secondary hover:text-agro-green flex items-center gap-1 disabled:opacity-50"
                data-testid="products-export-btn">
          <DownloadSimpleIcon size={14} /> Exportar
        </button>
        <label className="px-2.5 py-1.5 text-xs text-text-secondary hover:text-agro-yellow flex items-center gap-1 cursor-pointer">
          <UploadSimpleIcon size={14} /> Importar
          <input type="file" accept=".xlsx" onChange={onImport} disabled={busy}
                 className="hidden" data-testid="products-import-input" />
        </label>
      </div>
      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...rm.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg">Resultado de importación</h3>
              <BotonCerrar onClick={rm.cerrar} />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-app border border-border rounded-md p-3 text-center">
                <div className="text-2xl font-heading text-agro-green">{result.creados}</div>
                <div className="text-[10px] uppercase text-text-muted">Creados</div>
              </div>
              <div className="bg-app border border-border rounded-md p-3 text-center">
                <div className="text-2xl font-heading text-agro-yellow">{result.actualizados}</div>
                <div className="text-[10px] uppercase text-text-muted">Actualizados</div>
              </div>
              <div className="bg-app border border-border rounded-md p-3 text-center">
                <div className="text-2xl font-heading text-red-400">{result.errores?.length || 0}</div>
                <div className="text-[10px] uppercase text-text-muted">Errores</div>
              </div>
            </div>
            {result.errores?.length > 0 && (
              <table className="w-full text-xs">
                <thead><tr className="text-text-muted">
                  <th className="text-left p-1">Fila</th><th className="text-left p-1">Código</th><th className="text-left p-1">Error</th>
                </tr></thead>
                <tbody>
                  {result.errores.map((er, i) => (
                    <tr key={i} className="border-t border-border/30">
                      <td className="p-1">{er.fila}</td>
                      <td className="p-1 font-mono">{er.codigo || "—"}</td>
                      <td className="p-1 text-red-400">{er.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </>
  );
}

